import { useCallback, useMemo, useState } from "react";

import { zPropertyAttributeType } from "~/common/types";
import type { CategoryFormData, CategoryRaw, PropertySchemaRow } from "~/features/admin/categories/types";
import {
  buildParentCategoryOptions,
  findNearestRegisteredAncestor,
} from "~/features/admin/categories/utils/categoryPath";
import { findInheritedSchemaConflicts, type OwnSchemaSummary } from "~/features/admin/categories/utils/schemaConflicts";
import type { InheritedSchemaSummary } from "~/features/admin/propertySchemas";

interface UseInheritedCategorySchemasArgs {
  categories: readonly CategoryRaw[];
  /** The edited category's stored path (excluded from parent options); `null` when creating. */
  ownPath: string | null;
  /** Current form value of the parent path. */
  parentPath: string | null;
  /** Current form value of the own property-schema rows. */
  ownAssociations: CategoryFormData["propertySchemaAssociations"];
}

/**
 * Everything the category form derives from "which parent is selected":
 * the selectable parents, the ancestor to inherit from, the inherited rows
 * once loaded, and how the own rows relate to them.
 *
 * The inherited rows are tagged with the source they were loaded for, so a
 * parent switch is never judged against the previous parent's schemas while
 * the new ones are still loading (`inheritedReady` gates seeding and Save).
 */
export function useInheritedCategorySchemas({
  categories,
  ownPath,
  parentPath,
  ownAssociations,
}: UseInheritedCategorySchemasArgs) {
  const parentOptions = useMemo(
    () => buildParentCategoryOptions({ categories, excludeSubtreeOf: ownPath }),
    [categories, ownPath],
  );
  const inheritedSourceId = useMemo(
    () => findNearestRegisteredAncestor(parentPath, parentOptions)?.id ?? null,
    [parentPath, parentOptions],
  );

  const [inherited, setInherited] = useState<{ sourceCategoryId: string | null; rows: PropertySchemaRow[] }>();
  const onInheritedLoaded = useCallback(
    (sourceCategoryId: string | null, rows: PropertySchemaRow[]) => setInherited({ sourceCategoryId, rows }),
    [],
  );
  const inheritedReady = inherited !== undefined && inherited.sourceCategoryId === inheritedSourceId;
  const inheritedRows = useMemo(() => (inheritedReady ? inherited.rows : []), [inheritedReady, inherited]);

  const inheritedSchemas = useMemo(
    (): InheritedSchemaSummary[] =>
      inheritedRows.map((row) => {
        const parsed = zPropertyAttributeType.safeParse(row.propertyAttributeTypeId);
        return { propertyTypeId: row.propertyTypeId, propertyAttributeTypeId: parsed.success ? parsed.data : null };
      }),
    [inheritedRows],
  );

  const ownSchemas = useMemo(
    () =>
      (ownAssociations ?? []).flatMap((row): OwnSchemaSummary[] =>
        row.name
          ? [
              {
                name: row.name,
                propertyTypeId: row.propertySchema.propertyTypeId,
                propertyAttributeTypeId: row.propertyAttributeTypeId ?? null,
              },
            ]
          : [],
      ),
    [ownAssociations],
  );
  const ownSchemaNames = useMemo(() => ownSchemas.map((own) => own.name), [ownSchemas]);
  // Live check against the current parent's schemas: same key, different type/attribute → the parent wins.
  const conflicts = useMemo(() => findInheritedSchemaConflicts(ownSchemas, inheritedRows), [ownSchemas, inheritedRows]);

  return {
    parentOptions,
    inheritedSourceId,
    inheritedReady,
    inheritedSchemas,
    ownSchemaNames,
    conflicts,
    onInheritedLoaded,
  };
}
